import React, { Fragment } from 'react';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import { Link } from 'react-router';
import { languageMapRoute } from 'util/i18n';
import LogoIcon from 'images/icon/logo-font-black.inline.svg';
import signupBackground from 'images/signupBanner.png';
import loginBackground from 'images/banner-temp.png';

import Field from 'components/atoms/Field';
import Checkbox from 'components/atoms/Checkbox';
import Button from 'components/atoms/Button';
import Alert from 'components/atoms/Alert';

import { bindField } from 'util/bind';

import styles from './index.css';

const SignupEmailField = bindField('auth', 'signupForm', 'email')(Field);
const SignupPasswordField = bindField('auth', 'signupForm', 'password')(Field);
const SignupCheckPasswordField = bindField('auth', 'signupForm', 'checkPassword')(Field);
const AgreementCheckbox = bindField('auth', 'signupForm', 'agreement')(Checkbox);

const SignUpForm = ({ onSignup, onChangeToLogin, t, lan, onCloseLoginSignupModal }) => (
	<Fragment>
		<LogoIcon className={styles.logo} />
		<Alert className={classnames(styles.alert, styles.withlogo)} namespace="loginSignup" />
		<SignupEmailField
			className={styles.input}
			type="email"
			name="email"
			icon="person_outline"
			placeholder="Email"
		/>
		<SignupPasswordField
			className={styles.input}
			type="password"
			name="password"
			icon="lock_outline"
			placeholder="Password"
		/>
		<SignupCheckPasswordField
			className={styles.input}
			type="password"
			name="check-password"
			icon="lock_outline"
			placeholder="Confirm password"
		/>
		<span>
			{/* <Checkbox className={styles.checkbox} name="agree" value="agree" content="同意" /> */}
			<AgreementCheckbox
				className={styles.checkbox}
				name="agree"
				value="agree"
				content={t('agree')}
			/>
			<Link to={`/${languageMapRoute(lan)}/terms`} onClick={onCloseLoginSignupModal}>
				{t('termsofService')}
			</Link>
		</span>
		<Button className={styles.send} color="primary" onClick={onSignup}>
			{t('register')}
		</Button>
		<div className={styles.line} />
		<p className={styles.status}>
			{t('already')}
			<span onClick={onChangeToLogin} role="presentation">
				{t('loginNow')}
			</span>
		</p>
	</Fragment>
);

const SignUpSuccess = ({ onCloseLoginSignupModal, t }) => (
	<Fragment>
		<h4>{t('successful')}</h4>
		<p className={styles.left}>{t('verification_email')}</p>
		<Button className={styles.send} color="primary" link="/" onClick={onCloseLoginSignupModal}>
			{t('back')}
		</Button>
	</Fragment>
);

const LoginEmailField = bindField('auth', 'loginForm', 'email')(Field);
const LoginPasswordField = bindField('auth', 'loginForm', 'password')(Field);
const RememberCheckbox = bindField('auth', 'loginForm', 'remember')(Checkbox);

const LoginForm = ({
	onLogin,
	onChangeToSignup,
	onChangeToForget,
	alertType,
	onResendVerifyEmail,
	t,
}) => (
	<Fragment>
		<LogoIcon className={styles.logo} />
		<Alert className={classnames(styles.alert, styles.withlogo)} namespace="loginSignup">
			{alertType &&
				alertType === 'resendVerifyEmail' && (
					<Button onClick={onResendVerifyEmail}>{t('resend')}</Button>
				)}
		</Alert>
		<LoginEmailField
			className={styles.input}
			type="email"
			name="email"
			icon="person_outline"
			placeholder="Email"
		/>
		<LoginPasswordField
			className={styles.input}
			type="password"
			name="password"
			icon="lock_outline"
			placeholder="Password"
		/>
		<span>
			<RememberCheckbox
				className={styles.checkbox}
				name="remember"
				value="remember"
				content={t('rememberMe')}
			/>
		</span>
		<Button className={styles.send} color="primary" onClick={onLogin}>
			{t('login')}
		</Button>
		<p className={styles.forget}>
			{t('forgot')}
			<span onClick={onChangeToForget} role="presentation">
				{t('reset')}
			</span>
		</p>
		<div className={styles.line} />
		<p className={styles.status}>
			{t('not_a_member')}
			<span onClick={onChangeToSignup} role="presentation">
				{t('application')}
			</span>
		</p>
	</Fragment>
);

const ForgetEmailField = bindField('auth', 'forgetForm', 'email')(Field);

const ForgetPassword = ({ onSendForget, t }) => (
	<Fragment>
		<h4>{t('resetPw')}</h4>
		<Alert className={styles.alert} namespace="loginSignup" />
		<ForgetEmailField
			className={styles.input}
			type="email"
			name="email"
			icon="person_outline"
			placeholder="Email"
		/>
		<Button className={styles.send} color="primary" onClick={onSendForget}>
			{t('send')}
		</Button>
	</Fragment>
);

const NewPasswordField = bindField('auth', 'passwordForm', 'password')(Field);
const NewPasswordCheckField = bindField('auth', 'passwordForm', 'checkPassword')(Field);

const NewPassword = ({ onSetPassword, t }) => (
	<Fragment>
		<h4>{t('newPassword')}</h4>
		<Alert className={styles.alert} namespace="loginSignup" />
		<NewPasswordField
			className={styles.input}
			type="password"
			name="password"
			icon="lock_outline"
			placeholder={t('placeholder1')}
		/>
		<NewPasswordCheckField
			className={styles.input}
			type="password"
			name="password"
			icon="lock_outline"
			placeholder={t('placeholder2')}
		/>
		<Button className={styles.send} color="primary" onClick={onSetPassword}>
			{t('send')}
		</Button>
	</Fragment>
);

const SignupBanner = ({ t }) => (
	<div className={styles.banner} style={{ backgroundImage: `url(${signupBackground})` }}>
		<div className={styles.content}>
			<h4>{t('becomeResident')}</h4>
		</div>
	</div>
);

const LoginBanner = ({ t }) => (
	<div className={styles.banner} style={{ backgroundImage: `url(${loginBackground})` }}>
		<p className="vertical">
			{t('young')}
			<br />
			{t('year')}
		</p>
	</div>
);

const TransSignUpForm = translate('modalLoginSignup')(SignUpForm);
const TransSignUpSuccess = translate('modalLoginSignup')(SignUpSuccess);
const TransLoginForm = translate('modalLoginSignup')(LoginForm);
const TransNewPassword = translate('modalLoginSignup')(NewPassword);
const TransForgetPassword = translate('modalLoginSignup')(ForgetPassword);
const TransSignupBanner = translate('modalLoginSignup')(SignupBanner);
const TransLoginBanner = translate('modalLoginSignup')(LoginBanner);

const Signup = ({
	type,
	onSignup,
	onLogin,
	onChangeToLogin,
	onChangeToSignup,
	onChangeToForget,
	onSendForget,
	onSetPassword,
	alertType,
	onResendVerifyEmail,
	onCloseLoginSignupModal,
	lan,
}) => (
	<div className={styles.signupWrapper}>
		<div className={styles.form}>
			{type === 'signup' && (
				<TransSignUpForm
					onSignup={onSignup}
					onChangeToLogin={onChangeToLogin}
					onCloseLoginSignupModal={onCloseLoginSignupModal}
					lan={lan}
				/>
			)}
			{type === 'signupSuccess' && (
				<TransSignUpSuccess onCloseLoginSignupModal={onCloseLoginSignupModal} />
			)}
			{type === 'loginForm' && (
				<TransLoginForm
					alertType={alertType}
					onLogin={onLogin}
					onChangeToSignup={onChangeToSignup}
					onChangeToForget={onChangeToForget}
					onResendVerifyEmail={onResendVerifyEmail}
				/>
			)}
			{type === 'forgetPassword' && <TransForgetPassword onSendForget={onSendForget} />}
			{type === 'newPassword' && <TransNewPassword onSetPassword={onSetPassword} />}
		</div>
		{type === 'signup' ? <TransSignupBanner /> : <TransLoginBanner />}
	</div>
);

export default translate('modalLoginSignup')(Signup);



// WEBPACK FOOTER //
// ./src/components/molecules/Signup/component.js